<!--
  One row of listTaskDialAttempts -- every dial the campaign made, connected
  or not. Reuses CallHistoryRow.vue's atomic building blocks (the
  ch-row__status-badge/ch-row__field-label/ch-row__empty/ch-row__duration-badge
  classes from list.css) so the two rows share the same visual language, but
  its own grid/columns live in dial-attempts.css -- the fields here (caller +
  comments subtitle, outcome instead of direction+status, no "From number")
  don't map onto CallHistoryRow's fixed 7-column layout.

  COMPLETED rows are clickable straight through to CallIntelligenceDrawer,
  exactly like CallHistoryRow -- NOT_ANSWERED rows never had a recording to
  begin with, so the row is intentionally inert (no click handler, no ghost
  play button) with a plain "No recording" label instead.
-->
<template>
  <article
    class="da-row"
    :class="{ 'da-row--clickable': isConnected }"
    role="listitem"
    :tabindex="isConnected ? 0 : -1"
    @click="onRowClick"
    @keydown.enter="onRowClick"
  >
    <div class="ch-row__time">
      <v-icon icon="mdi-calendar-clock-outline" size="16" class="ch-row__time-icon" />
      <div class="ch-row__time-text">
        <time class="ch-row__date" :title="absoluteTimeLabel">{{ relativeTimeLabel }}</time>
        <span class="ch-row__clock">{{ clockLabel }}</span>
      </div>
    </div>

    <div class="da-row__caller">
      <span class="ch-row__field-label">Caller</span>
      <span class="da-row__caller-name" :title="call.caller_name">{{ call.caller_name }}</span>
      <span class="da-row__caller-number">{{ call.caller_number }}</span>
    </div>

    <div class="da-row__outcome">
      <span class="ch-row__field-label">Outcome</span>
      <span class="ch-row__status-badge" :class="outcomeBadgeClass">
        <v-icon :icon="outcomeIcon" size="13" class="da-row__outcome-icon" />
        {{ outcomeLabel }}
      </span>
    </div>

    <div class="da-row__comments">
      <span class="ch-row__field-label">Notes</span>
      <span
        v-if="call.comments"
        class="da-row__comments-text"
        :title="call.comments"
      >{{ call.comments }}</span>
      <span v-else class="ch-row__empty">No notes</span>
    </div>

    <div class="ch-row__duration">
      <span class="ch-row__field-label">Duration</span>
      <span v-if="isConnected && durationLabel !== '—'" class="ch-row__duration-badge">
        {{ durationLabel }}
      </span>
      <span v-else class="ch-row__empty">—</span>
    </div>

    <div class="ch-row__actions">
      <button
        v-if="isConnected"
        type="button"
        class="ch-row__open"
        aria-label="View conversation intelligence"
        @click.stop="onRowClick"
      >
        <v-icon icon="mdi-chevron-right" size="18" />
      </button>
      <span v-else class="da-row__no-recording">
        <v-icon icon="mdi-microphone-off" size="13" />
        No recording
      </span>
    </div>
  </article>
</template>

<script setup>
import { computed } from 'vue'
import { formatCallDuration, formatDateTime, formatRelativeTime, formatTimeAmPm } from '@/utils/format'

defineOptions({ name: 'DialAttemptRow' })

const props = defineProps({
  call: { type: Object, required: true },
})

const emit = defineEmits(['open-detail'])

const isConnected = computed(() => props.call.outcome === 'COMPLETED')

const relativeTimeLabel = computed(() => formatRelativeTime(props.call.created_on))
const absoluteTimeLabel = computed(() => formatDateTime(props.call.created_on))
const clockLabel = computed(() => formatTimeAmPm(props.call.created_on))
const durationLabel = computed(() => formatCallDuration(props.call.duration_seconds))

const outcomeLabel = computed(() => (isConnected.value ? 'Connected' : 'Not answered'))
const outcomeIcon = computed(() =>
  isConnected.value ? 'mdi-phone-check-outline' : 'mdi-phone-missed',
)
const outcomeBadgeClass = computed(() =>
  isConnected.value ? 'ch-row__status-badge--success' : 'ch-row__status-badge--danger',
)

function onRowClick() {
  if (!isConnected.value) return
  emit('open-detail', props.call)
}
</script>
